import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Box, Button, Container, Typography, Divider } from "@mui/material";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import BusinessCenterOutlinedIcon from "@mui/icons-material/BusinessCenterOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import ExtensionOutlinedIcon from "@mui/icons-material/ExtensionOutlined";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import FilterVintageOutlinedIcon from "@mui/icons-material/FilterVintageOutlined";
import AltRouteOutlinedIcon from "@mui/icons-material/AltRouteOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import HaveQuestions from "../components/HaveQuestions";
import GradientIcon from "../components/GradientIcon";
import caseStudyBg from "../assets/CaseStudy/caseStudy-bg.png";
import halfCircles from "../assets/half-circles.png";
import caseStudyApp from "../assets/CaseStudy/case-study-app-screenshot.png";
import caseStudyApp1 from "../assets/CaseStudy/case-study-app-screenshot1.png";
import damRepoScreenshot from "../assets/CaseStudy/case-study-dam-repository-screenshot.png";
import damRepoScreenshot1 from "../assets/CaseStudy/case-study-dam-repository-screenshot1.png";

const gradientHoverSx = {
  position: "relative",
  border: "2px solid transparent",
  backgroundClip: "padding-box",
  transition: "all 0.3s ease",
  "&:hover::before": {
    opacity: 1,
  },
  "&::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    borderRadius: "16px",
    padding: "2px",
    background: "linear-gradient(90deg, #FF7FAC, #5834BA)",
    WebkitMask:
      "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
    WebkitMaskComposite: "xor",
    maskComposite: "exclude",
    opacity: 0,
    transition: "opacity 0.3s ease",
  },
};

const ApproachCard = ({
  title,
  description,
  icon = CheckCircleOutlinedIcon,
  items,
}) => (
  <Box
    sx={{
      width: "408px",
      height: "auto",
      minHeight: "306px",

      "@media (max-width: 1400px)": {
        width: "100%",
        height: "auto",
        minHeight: "326px",
      },
      "@media (max-width: 819px)": {
        width: "100%",
        height: "auto",
      },
      px: "16px",
      py: "24px",
      borderRadius: "16px",
      background: "linear-gradient(135deg, #FC86AF0D, #724FD30D)",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      ...gradientHoverSx,
    }}
  >
    <GradientIcon
      icon={icon}
      size={40}
      bordered
      boxSize={64}
      borderRadius={16}
      bgColor="transparent"
    />
    <Typography
      sx={{
        fontSize: "20px",
        fontWeight: 700,
        color: "#2A2177",
        mt: 1,
        mb: 1,
        alignSelf: "flex-start",
        letterSpacing: "-0.4px",
      }}
    >
      {title}
    </Typography>
    <Typography
      sx={{
        fontSize: "0.875rem",
        color: "#00000099",
        lineHeight: 1.34,
        alignSelf: "flex-start",
      }}
    >
      {description}
    </Typography>
    {items && items.length > 0 && (
      <Box
        component="ul"
        sx={{
          pl: 2,
          m: 0,
          mt: 2,
          alignSelf: "flex-start",
          listStyleType: "'•  '",
          "& li": {
            fontSize: "0.875rem",
            color: "#00000099",
            lineHeight: 1.43,
          },
        }}
      >
        {items.map((item, index) => (
          <li key={index}>{item}</li>
        ))}
      </Box>
    )}
  </Box>
);

const CaseStudy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <Box sx={{ backgroundColor: "#FFFFFF" }}>
      <Helmet>
        <title>DAM Journey - Case Study</title>
        <meta
          name="description"
          content="Build and Roll out an Enterprise-grade Digital Assets Management Repository for a Financial Services Client, Build an enterprise-grade, scalable digital assets management platform that can be utilized by multiple vertical teams of digital assets creators."
        />
        <meta
          property="keywords"
          content="DAM, DAM Journey, DAM Practice, DAM Consultant, DAM Consulting, DAM Practitioner, DAM Service, DAM Product, DAM Discovery, DAM Implementation, DAM Deployment, Build and Roll out an Enterprise-grade Digital Assets Management Repository for a Financial Services Client"
        />
      </Helmet>
      <Box
        sx={
          {
            /*maxWidth: "1440px"*/
          }
        }
      >
        {/* Hero Banner Section */}
        <Box
          sx={{
            width: "100%",
            minHeight: "350px",
            background: "linear-gradient(0deg, #2A2177 0%, #0624BE 100%)",
            display: "flex",
            alignItems: "center",
            px: "84px",
            py: "54px",
            overflow: "hidden",
            position: "relative",
            "@media (max-width: 1400px)": {
              px: "24px",
              py: "48px",
            },
            "@media (max-width: 819px)": {
              flexDirection: "column",
              textAlign: "center",
              px: "16px",
              pt: "48px",
              pb: 2,
              minHeight: "420px",
              height: "auto",
            },
          }}
        >
          {/* Left Text Content */}
          <Box
            sx={{
              flex: 2,
              zIndex: 1,
              gap: 2,
              maxWidth: "1000px",

              "@media (max-width: 1400px)": {
                maxWidth: "520px",
              },

              "@media (max-width: 819px)": {
                maxWidth: "100%",
              },
            }}
          >
            <Typography
              sx={{
                fontSize: "0.875rem",
                fontWeight: "regular",
                color: "#ffffff",
                lineHeight: 2,
                maxWidth: "624px",
              }}
            >
              CASE STUDY
            </Typography>
            <Typography
              variant="h1"
              sx={{
                fontSize: "2.5rem",
                fontWeight: 700,
                color: "#ffffff",
                lineHeight: 1.3,
                mb: 2,
                "@media (max-width: 1400px)": {
                  fontSize: "1.75rem",
                  fontWeight: "semiBold",
                },
                "@media (max-width: 819px)": {
                  fontSize: "1.5rem",
                  mb: 1,
                },
              }}
            >
              Build and Roll out an Enterprise-grade Digital Assets Management
              Repository for a Financial Services Client
            </Typography>
            <Typography
              sx={{
                fontSize: "20px",
                fontWeight: "regular",
                color: "#ffffff",
                lineHeight: 1.7,
                maxWidth: "624px",
                "@media (max-width: 1400px)": {
                  fontSize: "18px",
                },
                "@media (max-width: 819px)": {
                  fontSize: "16px",
                },
              }}
            >
              Build an enterprise-grade, scalable digital assets management
              platform that can be utilized by multiple vertical teams of
              digital assets creators.
            </Typography>
          </Box>

          {/* Right Infographic Image */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              position: "relative",
              "@media (max-width: 1400px)": {
                justifyContent: "flex-end",
              },
            }}
          >
            {/* Background Half Circles */}
            <Box
              component="img"
              src={halfCircles}
              aria-hidden="true"
              sx={{
                position: "absolute",
                width: "599px",
                height: "350px",
                objectFit: "contain",
                left: -100,
                "@media (max-width: 1400px)": {
                  display: "none",
                },
                zIndex: 0,
              }}
            />
            {/* Foreground caseStudy-bg */}
            <Box
              component="img"
              src={caseStudyBg}
              sx={{
                position: "relative",
                zIndex: 1,
                width: "515px",
                height: "290px",
                "@media (max-width: 1400px)": {
                  width: "355px",
                  height: "200px",
                },
                "@media (max-width: 819px)": {
                  fontSize: "18px",
                  width: "265px",
                  height: "149px",
                },
              }}
            />
          </Box>
        </Box>

        {/* The Challenge Section */}
        <Box
          sx={{
            minHeight: "462px",
            px: "84px",
            "@media (max-width: 1400px)": {
              px: "24px",
            },
            "@media (max-width: 819px)": {
              px: "16px",
            },
            pt: "48px",
            pb: "24px",
            boxSizing: "border-box",
          }}
        >
          {/* Section Header */}
          <Typography
            sx={{
              fontSize: "3rem",
              "@media (max-width: 1400px)": {
                fontSize: "2.5rem",
              },
              "@media (max-width: 819px)": {
                fontSize: "2rem",
              },
              fontWeight: 700,
              color: "#2A2177",
              lineHeight: 1.2,
              mb: 4,
            }}
          >
            The Challenge
          </Typography>

          {/* Content Row  */}
          <Box
            sx={{
              display: "flex",
              gap: 3,
              alignItems: "flex-start",
              "@media (max-width: 1400px)": {
                flexDirection: "column",
              },
            }}
          >
            {/* Left Column */}
            <Box sx={{ flex: 2 }}>
              {/* Description Text */}
              <Typography
                sx={{
                  fontSize: "18px",
                  color: "#333333",
                  lineHeight: 1.7,
                  mb: "20px",
                }}
              >
                The client, a leader in the financial services sector, was
                struggling with fragmented digital assets across departments,
                inconsistent metadata, and slow approval workflows.
              </Typography>

              {/* Gray Box - Challenges List */}
              <Box
                sx={{
                  backgroundColor: "#F9F9F9",
                  borderRadius: "16px",
                  py: "24px",
                  pr: "16px",
                  pl: "24px",
                  display: "flex",
                  alignItems: "center",
                  maxWidth: "840px",
                  "@media (max-width: 1400px)": {
                    maxWidth: "100%",
                  },
                  "@media (max-width: 819px)": {
                    flexDirection: "column",
                    pl: "16px",
                    py: "24px",
                  },
                  gap: 3,
                }}
              >
                {/* Document Icon */}
                <Box>
                  <DescriptionOutlinedIcon
                    sx={{ width: 40, height: 40, color: "#2A2177" }}
                  />
                </Box>

                {/* Challenge Items */}
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {[
                    {
                      title: "Internal Folder",
                      text:
                        "Configure a private folder for a business group that should not be viewed by other business groups.",
                    },
                    {
                      title: "Diverse Tenants",
                      text:
                        "Many business groups with diverse assets inventory sizes and varied assets metadata requirements.",
                    },
                    {
                      title: "Project Management",
                      text: "Various onboarding timeline per group.",
                    },
                    {
                      title: "OOTB",
                      text:
                        "Stay within Out-of-the-Box in the AEM for all designs.",
                    },
                    {
                      title: "Assets Ownership",
                      text:
                        "Assets are owned by business groups along with their own groups and permissions.",
                    },
                  ].map((item, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1.5,
                      }}
                    >
                      <GradientIcon
                        icon={CheckCircleOutlinedIcon}
                        size={20}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mt: "3px",
                        }}
                      />
                      <Box>
                        <Typography
                          sx={{
                            fontSize: "1rem",
                            fontWeight: 700,
                            color: "#000000DE",
                          }}
                        >
                          {item.title}
                        </Typography>
                        <Typography
                          sx={{ fontSize: "1rem", color: "#00000099" }}
                        >
                          {item.text}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>

            {/* Right Box - Quick Facts */}
            <Box
              sx={{
                flex: 1,
                width: "408px",
                height: "374px",
                backgroundColor: "#FFFFFF",
                border: "1px solid #0000001A",
                borderRadius: "16px",
                boxShadow: "0px 4px 4px 0px #0000001A",
                p: 2,
                display: "flex",
                flexDirection: "column",
                "@media (max-width: 1400px)": {
                  width: "100%",
                },
              }}
            >
              {/* Quick Facts Header */}
              <Typography
                sx={{
                  fontSize: "1.25rem",
                  fontWeight: 700,
                  color: "#2A2177",
                  mb: 0,
                }}
              >
                Quick Facts
              </Typography>

              {/* Quick Facts Items */}
              {[
                {
                  icon: BusinessCenterOutlinedIcon,
                  label: "Industry",
                  value: "Financial Services",
                },
                {
                  icon: GroupsOutlinedIcon,
                  label: "Company Size",
                  value: "5,000+ Employees",
                },
                {
                  icon: ExtensionOutlinedIcon,
                  label: "Solution",
                  value: "Enterprise DAM Platform",
                },
                {
                  icon: ScheduleOutlinedIcon,
                  label: "Timeline",
                  value: "9 Months",
                },
                {
                  icon: BuildOutlinedIcon,
                  label: "Services",
                  value:
                    "Consulting, Discovery, Implementation, Integration, Training",
                },
              ].map((item, index) => (
                <Box
                  key={index}
                  sx={{
                    minHeight: "62px",
                    display: "flex",
                    alignItems: "center",
                    gap: 3,
                  }}
                >
                  {/* Circle Icon Container */}
                  <Box
                    sx={{
                      width: "46px",
                      height: "46px",
                      minWidth: "46px",
                      borderRadius: "50%",
                      backgroundColor: "#FAFAFA",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <GradientIcon
                      icon={item.icon}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                      size={32}
                    />
                  </Box>

                  {/* Text */}
                  <Box>
                    <Typography
                      sx={{
                        fontSize: "0.875rem",
                        fontWeight: 700,
                        color: "#000000DE",
                      }}
                    >
                      {item.label}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.875rem",
                        color: "#00000099",
                      }}
                    >
                      {item.value}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        {/* Our Approach Section */}
        <Box
          sx={{
            px: "84px",
            "@media (max-width: 1400px)": {
              px: "24px",
            },
            "@media (max-width: 819px)": {
              px: "16px",
            },
            pt: "48px",
            pb: "24px",
            boxSizing: "border-box",
          }}
        >
          {/* Section Header */}
          <Typography
            sx={{
              fontSize: "3rem",
              "@media (max-width: 1400px)": {
                fontSize: "2.5rem",
              },
              "@media (max-width: 819px)": {
                fontSize: "2rem",
              },
              fontWeight: 700,
              color: "#2A2177",
              lineHeight: 1.2,
              mb: 1,
            }}
          >
            Our Approach
          </Typography>

          {/* Row 1 Description */}
          <Typography
            sx={{
              fontSize: "18px",
              color: "#00000099",
              lineHeight: 1.7,
              maxWidth: "840px",
              mb: 3,
            }}
          >
            We implemented a scalable unified DAM solution tailored to client’s
            assets limitations.
          </Typography>

          {/* Row 1 - Three Cards */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              "@media (max-width: 1400px)": {
                flexDirection: "column",
              },
              gap: 3,
              mb: 4,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: 3,
                "@media (min-width: 1400px)": {
                  flex: 2,
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                },
                "@media (max-width: 819px)": {
                  flexDirection: "column",
                },
              }}
            >
              {/* Card 1 - Strategic Foundation */}
              <ApproachCard
                icon={AltRouteOutlinedIcon}
                title="How We Did This"
                description="We followed a structured onboarding methodology that enabled each business group to adopt the DAM enterprise platform efficiently."
                items={[
                  "Conducted discovery through a standardized DAM Intake Questionnaire",
                  "Followed a phased delivery approach: Discovery, Design, Migration, Training, and Go-Live.",
                  "Managed development through two-week sprints using Jira.",
                  "Worked with 11 business groups through dedicated engagement tracks.",
                  "Developed master requirements and custom requirements accommodating each group's unique business needs.",
                ]}
              />

              {/* Card 2 - Solution in Action */}
              <ApproachCard
                icon={BadgeOutlinedIcon}
                title="Our Role"
                description="We led the end-to-end DAM implementation, governance, and onboarding effort across multiple business units."
                items={[
                  "Led discovery workshops with 11 business groups.",
                  "Designed the enterprise DAM information architecture, metadata model, and onboarding strategy.",
                  "Authored requirements, user stories, and managed backlog refinement.",
                  "Planned and coordinated asset migration, permissions, and governance.",
                  "Performed end-to-end solution testing and developed enterprise reporting for taxonomy and tag usage.",
                  "Produced business and technical documentation while representing the DAM initiative within the AEM Scrum team.",
                ]}
              />
            </Box>
            {/* Card 3 - Screenshot */}
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Box
                component="img"
                src={damRepoScreenshot}
                sx={{
                  width: "408px",
                  height: "504px",
                  "@media (max-width: 402px)": {
                    width: "100%",
                    height: "auto",
                  },
                  objectFit: "cover",
                  borderRadius: "16px",
                }}
              />
            </Box>
          </Box>
        </Box>

        {/* The Results Section */}
        <Box
          sx={{
            px: "84px",
            "@media (max-width: 1400px)": {
              px: "24px",
            },
            "@media (max-width: 819px)": {
              px: "16px",
            },
            pt: "48px",
            pb: "24px",
            boxSizing: "border-box",
          }}
        >
          {/* Section Header */}
          <Typography
            sx={{
              fontSize: "3rem",
              "@media (max-width: 1400px)": {
                fontSize: "2.5rem",
              },
              "@media (max-width: 819px)": {
                fontSize: "2rem",
              },
              fontWeight: 700,
              color: "#2A2177",
              lineHeight: 1.2,
              mb: 1,
            }}
          >
            The Results
          </Typography>

          {/* Row 1 Description */}
          <Typography
            sx={{
              fontSize: "18px",
              color: "#00000099",
              lineHeight: 1.7,
              maxWidth: "840px",
              mb: 3,
            }}
          >
            The organization achieved running a structured digital assets for
            all business groups and onboarded groups’ assets through a phased
            schedule.
          </Typography>

          {/* Row 1 - Three Cards */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              "@media (max-width: 1400px)": {
                flexDirection: "column",
              },
              gap: 3,
              mb: 4,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: 3,
                "@media (min-width: 1400px)": {
                  flex: 2,
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                },
                "@media (max-width: 819px)": {
                  flexDirection: "column",
                },
              }}
            >
              {/* Card 1 - Strategic Foundation */}
              <ApproachCard
                title="Business Benefits"
                description=""
                items={[
                  "Structured content folder owned by business groups.",
                  "Ownership control over their assets.",
                  "Top-level node in asset folders in DAM and Tags, compartmentalizing them from default AEM folders for easier assets management.",
                ]}
              />

              {/* Card 2 - Solution in Action */}
              <ApproachCard
                title="What We Delivered"
                description=""
                items={[
                  "Designed an enterprise content structure that organized assets under a scalable client hierarchy.",
                  "Created a scalable onboarding framework that accelerated adoption for current and future business groups.",
                ]}
              />
            </Box>
            {/* Card 3 - Screenshot */}
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Box
                component="img"
                src={damRepoScreenshot1}
                sx={{
                  width: "408px",
                  height: "306px",
                  "@media (max-width: 402px)": {
                    width: "100%",
                    height: "auto",
                  },
                  objectFit: "cover",
                  borderRadius: "16px",
                }}
              />
            </Box>
          </Box>
        </Box>

        {/* Have Questions Section */}
        <HaveQuestions />
      </Box>
    </Box>
  );
};

export default CaseStudy;
